import React, { createContext, useContext, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DRAG_HANDLE } from '../icons';

// ---- a manual order (KPR-1396) -------------------------------------------
//
// A list sorted by a rank element reorders by drag: each row is a dnd-kit
// sortable, and the grip in its first cell is the only thing that starts a
// drag (a MouseSensor, so a touch tap stays a tap; a phone has the row
// menu's Move up / Move down / Move to top). The grip also answers ↑ and ↓
// from the keyboard, as the menu does.
const RankRowContext = createContext(null);
export const RANK_HANDLE_WIDTH = 40;

/** antd's body row, as a sortable: its own transform while it is dragged. */
export function RankSortableRow(props) {
	const id = props['data-row-key'];
	const { setNodeRef, setActivatorNodeRef, listeners, transform, transition, isDragging } = useSortable({ id: id ? `${id}` : '__none', disabled: !id });
	const style = {
		...props.style,
		transform: CSS.Translate.toString(transform),
		transition,
		...(isDragging ? { position: 'relative', zIndex: 2 } : {}),
	};
	const handle = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
	return (
		<RankRowContext.Provider value={handle}>
			<tr {...props} ref={setNodeRef} style={style} className={[props.className, isDragging ? 'items-table__row--dragging' : ''].filter(Boolean).join(' ')} />
		</RankRowContext.Provider>
	);
}

/** The grip: a drag by mouse, ↑ / ↓ by keyboard; disabled (with the reason) when the row cannot move. */
export function RankHandle({ title, reason, onStep }) {
	const ctx = useContext(RankRowContext);
	if (reason) {
		return (
			<span className="items-table__rank-handle items-table__rank-handle--off" title={reason} aria-hidden="true">
				<DRAG_HANDLE />
			</span>
		);
	}
	return (
		<button
			type="button"
			ref={ctx?.setActivatorNodeRef}
			className="items-table__rank-handle"
			aria-label={`Move ${title}. Use the up and down arrow keys.`}
			title="Drag to reorder"
			onClick={event => event.stopPropagation()}
			onKeyDown={(event) => {
				if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
				if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
				event.preventDefault();
				event.stopPropagation();
				onStep(event.key === 'ArrowUp' ? 'up' : 'down');
			}}
			{...(ctx?.listeners || {})}
		>
			<DRAG_HANDLE aria-hidden="true" />
		</button>
	);
}
